import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../styles/components/_listapropostas.scss";
import ProposalProvider from "../../infra/provider/proposalProvider";
import { Formatter } from "../../utils/formatter";
import ProposalStatus from "../../domain/entities/valueObjects/proposalStatus";
import { toast, ToastContainer } from "react-toastify";
import { ListSearch } from "../../domain/entities/valueObjects/listSearch";
import Tooltip from "../tootip/Tootip";
import { FilterX, Search, Eye, Pencil } from "lucide-react";

interface TableData {
  id: string;
  createdAt: string;
  name: string;
  document: string;
  phone: string;
  email: string;
  enterpriseName: string;
  status: string;
  rawStatus: string;
}

interface ProposalFilters {
  search: string;
  status: string;
  fromDate: string;
  toDate: string;
}

const Proposals = () => {
  const [data, setData] = useState<TableData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [fetchProposals, setFetchProposals] = useState(true);

  const [filters, setFilters] = useState<ProposalFilters>({
    search: '',
    status: '',
    fromDate: '',
    toDate: ''
  });

  const navigate = useNavigate();
  const isFetching = useRef(false);
  const proposalProvider = ProposalProvider.getInstance();
  const [searchTrigger, setSearchTrigger] = useState(false);

  const handleFilterChange = (key: keyof ProposalFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
    setFetchProposals(true);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const search = new ListSearch({
        searchTerm: filters.search,
        status: filters.status || undefined,
        orderBy: "createdAt",
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
        fromDate: filters.fromDate ? Formatter.dateStringToBrtTimestamp(filters.fromDate) : undefined,
        toDate: filters.toDate ? Formatter.dateStringToBrtTimestamp(filters.toDate) : undefined,
      });

      const response = await proposalProvider.getProposals(search);
      const formattedData = response.proposals.map((item) => ({
        id: item.id,
        createdAt: Formatter.timestampToDateBR(item.createdAt),
        name: item.lead.name,
        document: item.lead.document,
        phone: item.lead.phone,
        email: item.lead.email,
        enterpriseName: item.enterprise.name,
        status: item.status.getFormatted(),
        rawStatus: item.status.getStatus(),
      }));
      setData(formattedData);
      setTotalPages(Math.ceil(response.totalItems / itemsPerPage));
    } catch (error) {
      console.error('Erro ao buscar propostas:', error);
      toast.error('Erro ao carregar as propostas');
    } finally {
      setIsLoading(false);
      setFetchProposals(false);
    }
  };

  useEffect(() => {
    if (fetchProposals) {
      fetchData();
    }
  }, [fetchProposals]);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      fromDate: '',
      toDate: ''
    });
    setCurrentPage(1);
    setFetchProposals(true);
  };

  const handleRowClick = (id: string) => {
    navigate(`/propostas/${id}`);
  };

  const formatCPF = (document: string) =>
    document.replace(/\D/g, "").replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");

  const formatPhone = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 13) {
      return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 4)}) ${cleaned.slice(4, 8)}-${cleaned.slice(8)}`;
    } else if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const handleViewDetails = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/propostas/${id}`);
  };

  const handleEditProposal = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/propostas/editar/${id}`);
  };

  return (
    <div className="data-table-container">
      <div className="filters-container">
        <div className="search-container">
          <input
            type="text"
            placeholder="Busque por um nome, CPF, ID etc..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="status-select"
        >
          <option value="">Todos</option>
          {ProposalStatus.getAllStatus().map((status) => (
            <option key={status} value={status}>
              {new ProposalStatus(status).getFormatted()}
            </option>
          ))}
        </select>
        <div className="date-filter">
          <label className="data-picker-proposal">De:</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            className="date-input"
          />
          <label className="data-picker-proposal">Até:</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            className="date-input"
          />
        </div>
        <Tooltip text="Limpar">
          <button className="clear-filters-button" onClick={handleClearFilters}>
            <FilterX size={16} />
          </button>
        </Tooltip>
      </div>
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Data de criação</th>
              <th>Nome</th>
              <th>Celular</th>
              <th>E-mail</th>
              <th>Empreendimento</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  Carregando...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  Nenhum resultado encontrado.
                </td>
              </tr>
            ) : (
              data.map((item, index) => (
                <tr key={index}>
                  <td>{item.id}</td>
                  <td>{item.createdAt}</td>
                  <td>
                    <div style={{ fontWeight: 'bold' }}>{item.name}</div>
                    {item.document && (
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {formatCPF(item.document)}
                      </div>
                    )}
                  </td>
                  <td>{item.phone && formatPhone(item.phone)}</td>
                  <td>{item.email}</td>
                  <td>{item.enterpriseName}</td>
                  <td>
                    <span className={`status-badge status-${item.rawStatus}`}>
                      {item.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Tooltip text="Detalhes">
                        <button
                          className="action-button"
                          onClick={(e) => handleViewDetails(item.id, e)}
                        >
                          <Eye size={20} />
                        </button>
                      </Tooltip>
                      <Tooltip text="Editar">
                        <button
                          className="action-button"
                          onClick={(e) => handleEditProposal(item.id, e)}
                        >
                          <Pencil size={20} />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="table-footer">
        <div className="items-per-page">
          <span>Exibir por</span>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={30}>30</option>
          </select>
        </div>
        <div className="pagination">
          <button onClick={() => {
            setFetchProposals(true);
            setCurrentPage((prev) => Math.max(prev - 1, 1));
          }}
            disabled={currentPage === 1}
          >
            Anterior
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => {
                setFetchProposals(true);
                setCurrentPage(page);
              }}
              className={currentPage === page ? "active" : ""}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => {
              setFetchProposals(true);
              setCurrentPage((prev) => Math.min(prev + 1, totalPages));
            }}
            disabled={currentPage === totalPages}
          >
            Próximo
          </button>
        </div>
      </div>
      <ToastContainer />
    </div >
  );
};

export default Proposals;
